import React from 'react';
import {Box} from '@mui/material';
import {
  DataGridPro,
  DataGridProProps,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import moment from 'moment';
import {useNavigate, useParams} from 'react-router-dom';
import {ScrollableModal} from '../../../components/Common/ScrollableModal';
import {SingleInvitation} from '../SingleInvitation';
import {useGetInvitations} from '../invitationQueries';
import {
  ContactATRECQStatusEnum,
  InvitationReviewsInput,
} from '../../../schema-types/globalTypes';
import {ReviewInvitationSummaryFragment} from '../../../schema-types/ReviewInvitationSummaryFragment';
import ErrorMessage from '../../../components/ErrorMessage/ErrorMessage';
import {useDebounce} from '../../../hooks/useDebounce';
import {NoRowsOverlay} from './NoRowsOverlay';
import {LoadingOverlay} from './LoadingOverlay';
import {themeColors} from '../../../theme/Theme';

interface InvitationDataGridProps extends DataGridProProps {
  inviteStatus: InvitationReviewsInput;
  staff?: string;
  search?: string;
  ecqStatus?: ContactATRECQStatusEnum | 'ALL';
  atrStatus?: ContactATRECQStatusEnum | 'ALL';
  columns: GridColDef<ReviewInvitationSummaryFragment>[];
  entityName: string;
  noDataMessage: string;
}

export const InvitationDataGrid: React.FC<InvitationDataGridProps> = ({
  inviteStatus,
  staff,
  search,
  ecqStatus,
  atrStatus,
  columns,
  entityName,
  noDataMessage,
  ...dataGridProps
}) => {
  const debouncedSearch = useDebounce(search, 500);
  const navigate = useNavigate();
  // Note: modal display is based on url, not state.
  // If there's a reviewId in the url, the modal will be open automatically
  // If there's no reviewId in the url, the modal will be closed automatically

  const {reviewId} = useParams();

  const {data, error, loading} = useGetInvitations({
    inviteStatus,
    staff,
    search: debouncedSearch,
    ecqStatus: ecqStatus === 'ALL' ? undefined : ecqStatus,
    atrStatus: atrStatus === 'ALL' ? undefined : atrStatus,
  });

  const handleRowClick = (params: GridRowParams<ReviewInvitationSummaryFragment>) => {
    const review = params.row;
    const newReviewId = review.id;

    if (newReviewId) {
      navigate(`./${newReviewId}`, {relative: 'path'});
    } else {
      console.warn('Cannot open modal: missing reviewId', {reviewId: newReviewId});
    }
  };

  const handleCloseModal = () => {
    navigate('../', {relative: 'path'});
  };

  // Error state
  if (error) {
    return <ErrorMessage error={error} />;
  }

  // No data state
  if (data && !data?.getInvitationReviews?.reviews) {
    return <Box>{noDataMessage}</Box>;
  }

  const reviews = data?.getInvitationReviews?.reviews;

  return (
    <>
      <Box sx={{flex: 1, width: '100%'}} bgcolor="white" borderRadius={2}>
        <DataGridPro
          rows={reviews || []}
          loading={loading}
          slots={{
            loadingOverlay: LoadingOverlay(entityName),
            noRowsOverlay: NoRowsOverlay(entityName),
          }}
          hideFooter
          treeData={inviteStatus === 'TO_INVITE'}
          getTreeDataPath={(row) => {
            const relevantDate = row.dateScheduled || row.createdAt;
            const month = moment(relevantDate).format('MMMM YYYY');
            const accountName = row.account?.name || 'Unknown Account';
            // Include the full ID in the path for uniqueness, but we'll hide it in the display
            return [month, `${accountName}__${row.id}`];
          }}
          groupingColDef={{
            headerName: 'Account',
            valueFormatter: (value: unknown) => {
              if (typeof value === 'string' && value.includes('__')) {
                return value.split('__')[0];
              }
              return value as string;
            },
          }}
          defaultGroupingExpansionDepth={1}
          isGroupExpandedByDefault={(node) => {
            if (node.depth === 0) {
              return true;
            }
            return false;
          }}
          columns={columns}
          sortingOrder={['desc', 'asc']}
          initialState={{
            sorting: {
              sortModel: [
                {
                  // field: '__tree_data_group__',
                  field: 'dateScheduled',
                  sort: 'desc',
                },
              ],
            },
          }}
          disableRowSelectionOnClick
          onRowClick={handleRowClick}
          sx={{
            height: '35rem', //  this is the correct height agreed with Zoe
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: themeColors.lightBlue,
              // color: "red"
            },
            // Style only the top-level month divider rows (depth 0)
            '& .MuiDataGrid-row[data-rowindex][aria-level="1"]': {
              backgroundColor: themeColors.greyLight,
              color: 'black',
              fontWeight: 'bold',
            },
            '& .MuiDataGrid-row[data-rowindex][aria-level="1"]:hover': {
              backgroundColor: themeColors.greyLight,
              color: 'black',
              fontWeight: 'bold',
            },
            '& .MuiDataGrid-row[data-rowindex][aria-level="1"] .MuiDataGrid-cell': {
              backgroundColor: themeColors.greyLight,
              color: 'black',
              fontWeight: 'bold',
            },
            '& .MuiDataGrid-row[data-rowindex][aria-level="1"]:hover .MuiDataGrid-cell': {
              backgroundColor: themeColors.greyLight,
              color: 'black',
              fontWeight: 'bold',
            },
          }}
          {...dataGridProps}
        />
      </Box>

      <ScrollableModal
        open={!!reviewId}
        onClose={handleCloseModal}
        size="xlarge"
        autoHeight
        boxProps={{maxWidth: '1200px'}}
      >
        {reviewId ? (
          <SingleInvitation reviewId={reviewId} />
        ) : (
          <ErrorMessage error={{message: 'No review selected'}} />
        )}
      </ScrollableModal>
    </>
  );
};
